
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { DefaultConfiguration } from '@models/document/default-configuration.const';
import { RevokeConfiguration } from '@models/document/revoke-configuration.const';
import { InOutType } from '@models/generic/in-out-type.enum';

import {
  Article,
  ArticleOptions,
  ArticleString,
} from '@models/permissions/permission-type.enum';

import { PermissionDto } from '@models/permissions/permission.dto';
import { DocumentDto } from '@models/protocols/document.dto';

import { DocumentService } from '@services/document.service';
import { LoadingService } from '@services/loading.service';
import { MessageService } from '@services/message.service';
import { PermissionService } from '@services/permission.service';
import { ProtocolService } from '@services/protocol.service';

import { camelize } from '@utils/camelize.function';

import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

import { catchError } from 'rxjs';

@Component({
  selector: 'ebs-revoke-permission',
  templateUrl: './revoke-permission.component.html',
  styleUrls: ['./revoke-permission.component.scss'],
})
export class RevokePermissionComponent implements OnInit {

  public formGroup: FormGroup;

  public options = {
    plugins: {},
  };

  public selectedPermission:
    PermissionDto | null = null;

  public permissions: PermissionDto[] = [];

  public artOptions = ArticleOptions;

  public artOptionStrings = ArticleString;

  public saving = false;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private protocolService: ProtocolService,
    private documentService: DocumentService,
    private permissionService: PermissionService,
    private loader: LoadingService,
  ) {

    this.formGroup = new FormGroup({

      isDaily: new FormControl<boolean>(
        false,
        Validators.required,
      ),

      article: new FormControl<Article>(
        Article.Art27,
        Validators.required,
      ),

      delegate: new FormControl<boolean>(
        false,
        Validators.required,
      ),

      createdDate: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      permissionDate: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      startTime: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      endTime: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      revokeDate: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),
    });

    this.permissions = Array.isArray(config.data?.revokable)
      ? config.data.revokable
      : [];
  }

  public ngOnInit(): void {}

  public get hasRevokablePermissions(): boolean {
    return this.permissions.length > 0;
  }

  public cancel(): void {

    if (this.saving) {
      return;
    }

    this.ref.close(false);
  }

  public async confirm(): Promise<void> {

    if (
      this.saving ||
      !this.hasRevokablePermissions ||
      !this.selectedPermission
    ) {
      return;
    }

    const permission = this.selectedPermission;

    if (!permission.permissionProtocol?.protocolDate) {

      this.messageService.error(
        'Revoca permesso',
        'Il permesso selezionato non dispone di un protocollo valido',
      );

      return;
    }

    this.saving = true;
    this.loader.show();

    try {

      const values = {

        generationDate: camelize(
          new Date().toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            weekday: 'long',
          }),
        ),

        name:
          `${permission.delegate?.user?.role} ` +
          `${permission.delegate?.user?.name} ` +
          `${permission.delegate?.user?.lastName}`,

        registerNumber:
          permission.delegate?.user?.registerNumber,

        permissionDate: camelize(
          new Date(
            permission.permissionDate,
          ).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            weekday: 'long',
          }),
        ),

        validFor: permission.isDaily
          ? "l'intera giornata indicata"
          : `l'orario compreso tra le ore ` +
            `${new Date(permission.startTime)
              .getHours()
              .toString()
              .padStart(2, '0')}:` +
            `${new Date(permission.startTime)
              .getMinutes()
              .toString()
              .padStart(2, '0')} ` +
            `e le ore ` +
            `${new Date(permission.endTime)
              .getHours()
              .toString()
              .padStart(2, '0')}:` +
            `${new Date(permission.endTime)
              .getMinutes()
              .toString()
              .padStart(2, '0')}`,

        protocol:
          permission.permissionProtocol?.protocolId,

        protocolDate: camelize(
          new Date(
            permission.permissionProtocol.protocolDate,
          ).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            weekday: 'long',
          }),
        ),
      };

      const delegationBlob =
        await this.documentService.writePdfFromUrl(
          'assets/documents/revoke.pdf',
          RevokeConfiguration,
          values,
        );

      const firstUpload = {
        name:
          `FAISA-CISAL PISTOIA - Revoca Permesso Sindacale Retribuito del ` +
          `${permission.delegate?.user?.role} ` +
          `${permission.delegate?.user?.name} ` +
          `${permission.delegate?.user?.lastName} ` +
          `${permission.delegate?.user?.registerNumber}.pdf`,
        hasProtocol: false,
        isSigned: false,
        mimeType: 'application/pdf',
      };

      this.documentService
        .uploadFile(firstUpload, delegationBlob)
        .subscribe({

          next: (firstReturn: DocumentDto) => {

            this.protocolService
              .createProtocol([
                {
                  document: firstReturn.docId!,
                  type: InOutType.OUT,
                },
              ])
              .subscribe({

                next: async (result) => {

                  try {

                    const blob =
                      await this.documentService.writePdfFromBuffer(
                        delegationBlob,
                        DefaultConfiguration,
                        {
                          protocol:
                            `Protocollo N. ${result!.protocolId} del ` +
                            new Date().toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            }),

                          orgName:
                            'Segreteria Provinciale FAISA-CISAL di Pistoia',

                          type:
                            `${result!.type == InOutType.IN ? 'E' : 'U'}`,
                        },
                      );

                    permission.revokeProtocol = result;

                    this.permissionService
                      .revokePermission([permission])
                      .pipe(
                        catchError((err) => {
                          this.handleSaveError();
                          throw err;
                        }),
                      )
                      .subscribe({

                        next: () => {

                          this.documentService
                            .uploadFile(result.document!, blob)
                            .subscribe({

                              next: (res) => {

                                this.documentService
                                  .downloadFile(res)
                                  .subscribe({

                                    next: (blobResponse: any) => {

                                      const downloadLink =
                                        document.createElement('a');

                                      const objectUrl =
                                        window.URL.createObjectURL(
                                          blobResponse,
                                        );

                                      downloadLink.href = objectUrl;

                                      if (firstReturn.name) {
                                        downloadLink.setAttribute(
                                          'download',
                                          firstReturn.name,
                                        );
                                      }

                                      document.body.appendChild(
                                        downloadLink,
                                      );

                                      downloadLink.click();

                                      document.body.removeChild(
                                        downloadLink,
                                      );

                                      window.URL.revokeObjectURL(
                                        objectUrl,
                                      );

                                      this.loader.hide();
                                      this.saving = false;

                                      this.messageService.success(
                                        'Revoca permesso',
                                        'Permesso revocato con successo',
                                      );

                                      this.ref.close(true);
                                    },

                                    error: () => {
                                      this.handleSaveError();
                                    },
                                  });
                              },

                              error: () => {
                                this.handleSaveError();
                              },
                            });
                        },

                        error: () => {
                          // Errore già gestito dal catchError.
                        },
                      });

                  } catch {
                    this.handleSaveError();
                  }
                },

                error: () => {
                  this.handleSaveError();
                },
              });
          },

          error: () => {
            this.handleSaveError();
          },
        });

    } catch {
      this.handleSaveError();
    }
  }

  private handleSaveError(): void {

    this.loader.hide();
    this.saving = false;

    this.messageService.error(
      'Revoca permesso',
      'Errore durante la revoca, riprovare',
    );
  }
}